import { useEffect, useRef } from "react";

export function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  const tag = (el as HTMLElement).tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean((el as HTMLElement).isContentEditable);
}

function isSpace(e: KeyboardEvent): boolean {
  return e.key === " " || e.code === "Space";
}

/** Hold Space: keydown starts, keyup stops. Capture phase blocks Firefox scroll/button activation. */
export function usePttKeyBinding(
  handlers: { start: () => void; stop: () => void },
  enabled: boolean,
) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const holdingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const release = () => {
      if (!holdingRef.current) return;
      holdingRef.current = false;
      handlersRef.current.stop();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isSpace(e) || isTypingTarget(document.activeElement)) return;
      e.preventDefault();
      if (e.repeat || holdingRef.current) return;
      holdingRef.current = true;
      handlersRef.current.start();
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (!isSpace(e) || !holdingRef.current) return;
      e.preventDefault();
      release();
    };

    const capture = true;
    window.addEventListener("keydown", onKeyDown, capture);
    window.addEventListener("keyup", onKeyUp, capture);
    window.addEventListener("blur", release);
    return () => {
      window.removeEventListener("keydown", onKeyDown, capture);
      window.removeEventListener("keyup", onKeyUp, capture);
      window.removeEventListener("blur", release);
    };
  }, [enabled]);
}
